// Request log (SM-755): every inbound provider request — including those
// refused at authentication or validation — searchable by user,
// transaction, request id, client, endpoint, verdict, order key and time.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { AttemptsTable } from "@/components/domain/AttemptsTable";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { ENDPOINTS, VERDICTS } from "@/lib/constants";
import { endpointName, localToRfc3339, rfc3339ToLocal } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";

const FIELDS = ["user_id", "transaction_id", "request_id", "client_id", "endpoint", "verdict", "order_key", "from", "to"] as const;
type ReqSearch = Partial<Record<(typeof FIELDS)[number], string>>;

export const Route = createFileRoute("/_app/requests")({
  validateSearch: (s: Record<string, unknown>): ReqSearch => {
    const out: ReqSearch = {};
    for (const f of FIELDS) if (typeof s[f] === "string" && s[f]) out[f] = s[f] as string;
    return out;
  },
  component: RequestsPage,
});

function RequestsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/requests" });
  const [draft, setDraft] = useState<ReqSearch>(search);
  const reqs = useQuery({ queryKey: keys.requests(search), queryFn: () => fetchers.requests(search), refetchInterval: 10_000 });
  const set = (k: keyof ReqSearch) => (v: string) => setDraft((d) => ({ ...d, [k]: v || undefined }));

  return (
    <div>
      <PageHeader
        title="Requests"
        subtitle="The request log: one row per inbound provider request, written before its answer. Bodies, headers and signatures are never shown — only the body hash."
      />
      <form
        className="card mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          const clean: ReqSearch = {};
          for (const f of FIELDS) if (draft[f]) clean[f] = draft[f]!.trim();
          navigate({ search: clean });
        }}
      >
        <div className="form-grid">
          <Field label="User id">
            <TextInput value={draft.user_id ?? ""} onChange={(e) => set("user_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Transaction id">
            <TextInput value={draft.transaction_id ?? ""} onChange={(e) => set("transaction_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Request id">
            <TextInput value={draft.request_id ?? ""} onChange={(e) => set("request_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Client id">
            <TextInput value={draft.client_id ?? ""} onChange={(e) => set("client_id")(e.target.value)} className="mono" />
          </Field>
          <Field label="Endpoint">
            <Select value={draft.endpoint ?? ""} onChange={(e) => set("endpoint")(e.target.value)}>
              <option value="">Any</option>
              {ENDPOINTS.map((ep) => (
                <option key={ep} value={ep}>
                  {endpointName(ep)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Verdict">
            <Select value={draft.verdict ?? ""} onChange={(e) => set("verdict")(e.target.value)}>
              <option value="">Any</option>
              {VERDICTS.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </Select>
          </Field>
          <Field label="Order key" hint="broker_account_id|order_id">
            <TextInput value={draft.order_key ?? ""} onChange={(e) => set("order_key")(e.target.value)} className="mono" />
          </Field>
          <Field label="Received from">
            <TextInput type="datetime-local" step="1" value={rfc3339ToLocal(draft.from)} onChange={(e) => set("from")(localToRfc3339(e.target.value) ?? "")} />
          </Field>
          <Field label="Received before">
            <TextInput type="datetime-local" step="1" value={rfc3339ToLocal(draft.to)} onChange={(e) => set("to")(localToRfc3339(e.target.value) ?? "")} />
          </Field>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDraft({});
              navigate({ search: {} });
            }}
          >
            Clear
          </Button>
          <Button type="submit">Search</Button>
        </div>
      </form>
      <ErrorBanner error={reqs.error} />
      {reqs.isLoading ? <Skeleton lines={6} /> : <AttemptsTable rows={reqs.data ?? []} emptyText="No requests match." />}
    </div>
  );
}
